import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Loader2, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Currency } from "@/lib/currency";
import { format, addDays, addWeeks, addMonths } from "date-fns";

interface CreateCollectionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchantId: string;
  currency: Currency;
  terminology?: any;
  onSuccess?: () => void;
}

interface ServiceOption {
  id: string;
  name: string;
  description: string | null;
  fixed_amount: number | null;
  amount_type: string;
  frequency: string;
}

const FREQUENCY_OPTIONS = [
  { value: "one_time", label: "One Time" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
];

export function CreateCollectionDialog({
  open,
  onOpenChange,
  merchantId,
  currency,
  terminology = {},
  onSuccess,
}: CreateCollectionDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [clients, setClients] = useState<any[]>([]);
  const [services, setServices] = useState<ServiceOption[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingServices, setLoadingServices] = useState(false);

  const [formData, setFormData] = useState({
    selectedClientId: "",
    selectedServiceId: "",
    collectionName: "",
    description: "",
    amount: "",
    dueDate: format(addDays(new Date(), 7), "yyyy-MM-dd"),
    frequency: "one_time" as "one_time" | "weekly" | "monthly",
    numberOfPayments: 1,
    isAutoRecurring: false,
  });

  const serviceLabel = terminology?.products || "Service";

  // Fetch clients and services when dialog opens
  useEffect(() => {
    if (open && merchantId) {
      fetchClients();
      fetchServices();
    }
  }, [open, merchantId]);

  // Reset form when closed
  useEffect(() => {
    if (!open) {
      setFormData({
        selectedClientId: "",
        selectedServiceId: "",
        collectionName: "",
        description: "",
        amount: "",
        dueDate: format(addDays(new Date(), 7), "yyyy-MM-dd"),
        frequency: "one_time",
        numberOfPayments: 1,
        isAutoRecurring: false,
      });
    }
  }, [open]);

  const fetchClients = async () => {
    setLoadingClients(true);
    try {
      const { data, error } = await supabase
        .from("professional_clients")
        .select("id, name, email")
        .eq("merchant_id", merchantId)
        .eq("status", "active")
        .order("name");
      
      if (error) throw error;
      setClients(data || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
      toast.error("Failed to load clients");
    } finally {
      setLoadingClients(false);
    }
  };

  const fetchServices = async () => {
    setLoadingServices(true);
    try {
      const { data, error } = await supabase
        .from("professional_services")
        .select("id, name, description, fixed_amount, amount_type, frequency")
        .eq("merchant_id", merchantId)
        .eq("is_active", true)
        .order("name");
      
      if (error) throw error;
      setServices(data || []);
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoadingServices(false);
    }
  };

  const handleServiceChange = (serviceId: string) => {
    if (serviceId === "none") {
      setFormData(prev => ({ ...prev, selectedServiceId: "" }));
      return;
    }

    const service = services.find(s => s.id === serviceId);
    if (!service) return;

    const frequencyMap: Record<string, "one_time" | "weekly" | "monthly"> = {
      one_time: "one_time",
      weekly: "weekly",
      monthly: "monthly",
      quarterly: "monthly",
      annual: "monthly",
      staggered: "one_time",
    };

    setFormData(prev => ({
      ...prev,
      selectedServiceId: serviceId,
      collectionName: service.name,
      description: service.description || "",
      amount: service.amount_type === "fixed" && service.fixed_amount ? String(service.fixed_amount) : prev.amount,
      frequency: frequencyMap[service.frequency] || "one_time",
    }));
  };

  const handleSubmit = async () => {
    // Validation
    if (!formData.selectedClientId) {
      toast.error("Please select a client");
      return;
    }
    if (!formData.collectionName.trim()) {
      toast.error("Collection name is required");
      return;
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error("Amount must be greater than 0");
      return;
    }

    setIsLoading(true);
    try {
      const selectedClient = clients.find(c => c.id === formData.selectedClientId);
      if (!selectedClient) {
        toast.error("Client not found");
        return;
      }

      // Create subscription
      const { data: subscription, error: subError } = await supabase
        .from("client_subscriptions")
        .insert({
          client_id: formData.selectedClientId,
          merchant_id: merchantId,
          service_name: formData.collectionName.trim(),
          service_id: formData.selectedServiceId || null,
          description: formData.description.trim() || null,
          frequency: formData.frequency,
          is_auto_recurring: formData.isAutoRecurring,
          amount: formData.frequency === "one_time" 
            ? parseFloat(formData.amount)
            : parseFloat(formData.amount) * formData.numberOfPayments,
          currency,
          start_date: format(new Date(), "yyyy-MM-dd"),
          next_due_date: formData.dueDate,
        })
        .select()
        .single();

      if (subError) throw subError;

      // Create payment records
      const paymentRecords = [];
      const isOneTime = formData.frequency === "one_time";
      const numPayments = isOneTime ? 1 : formData.numberOfPayments;
      
      for (let i = 0; i < numPayments; i++) {
        let dueDate = new Date(formData.dueDate);
        if (i > 0) {
          if (formData.frequency === "weekly") {
            dueDate = addWeeks(dueDate, i);
          } else if (formData.frequency === "monthly") {
            dueDate = addMonths(dueDate, i);
          }
        }
        paymentRecords.push({
          subscription_id: subscription.id,
          client_id: formData.selectedClientId,
          merchant_id: merchantId,
          amount: parseFloat(formData.amount),
          currency,
          due_date: format(dueDate, "yyyy-MM-dd"),
          tranche_number: i + 1,
          tranche_label: isOneTime ? null : `${formData.frequency === "weekly" ? "Week" : "Month"} ${i + 1}`,
          status: "pending",
        });
      }

      const { data: payments, error: paymentError } = await supabase
        .from("client_payments")
        .insert(paymentRecords)
        .select("id, amount, due_date");

      if (paymentError) throw paymentError;

      // Send email for first payment
      if (payments && payments.length > 0) {
        const firstPayment = payments[0];
        try {
          await supabase.functions.invoke("send-collection-payment-link", {
            body: {
              payment_id: firstPayment.id,
              merchant_id: merchantId,
              client_id: formData.selectedClientId,
              subscription_id: subscription.id,
              amount: firstPayment.amount,
              currency,
              due_date: firstPayment.due_date,
              collection_name: formData.collectionName.trim(),
              description: formData.description.trim() || undefined,
            },
          });
        } catch (emailError) {
          console.error("Error sending email:", emailError);
        }
      }

      toast.success(`Collection created! Link sent to ${selectedClient.email}`);
      onOpenChange(false);
      onSuccess?.();
    } catch (error: any) {
      console.error("Error creating collection:", error);
      toast.error(error.message || "Failed to create collection");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Create Collection</DialogTitle>
          <DialogDescription>
            Send a payment request to a client
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="client">Client *</Label>
            <Select value={formData.selectedClientId} onValueChange={(value) => setFormData({ ...formData, selectedClientId: value })}>
              <SelectTrigger disabled={loadingClients}>
                <SelectValue placeholder="Select a client" />
              </SelectTrigger>
              <SelectContent>
                {clients.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name} ({client.email})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Service Selector */}
          <div className="space-y-2">
            <Label>{serviceLabel}</Label>
            <Select 
              value={formData.selectedServiceId || "none"} 
              onValueChange={handleServiceChange}
            >
              <SelectTrigger disabled={loadingServices}>
                <SelectValue placeholder={`Select a ${serviceLabel.toLowerCase()} (optional)`} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None (enter manually)</SelectItem>
                {services.map((service) => (
                  <SelectItem key={service.id} value={service.id}>
                    {service.name}
                    {service.amount_type === "fixed" && service.fixed_amount
                      ? ` — ${currency} ${service.fixed_amount}`
                      : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Collection Name *</Label>
            <Input
              id="name"
              value={formData.collectionName}
              onChange={(e) => setFormData({ ...formData, collectionName: e.target.value })}
              placeholder="e.g., Q1 Project Payment"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Add details about this payment..."
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="amount">Amount ({currency}) *</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="frequency">Frequency</Label>
              <Select value={formData.frequency} onValueChange={(value: any) => setFormData({ ...formData, frequency: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FREQUENCY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="dueDate">Due Date *</Label>
              <Input
                id="dueDate"
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              />
            </div>
            {formData.frequency !== "one_time" && !formData.isAutoRecurring && (
              <div className="space-y-2">
                <Label htmlFor="payments">Number of Payments *</Label>
                <Input
                  id="payments"
                  type="number"
                  min="1"
                  max="12"
                  value={formData.numberOfPayments}
                  onChange={(e) => setFormData({ ...formData, numberOfPayments: parseInt(e.target.value) })}
                />
              </div>
            )}
          </div>

          {/* Auto-Recurring Toggle */}
          {formData.frequency !== "one_time" && (
            <div className="flex items-center justify-between rounded-lg border p-3 bg-muted/30">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <RefreshCw className="h-4 w-4 text-cyan-600" />
                  <Label className="font-medium text-sm">Auto-create recurring</Label>
                </div>
                <p className="text-xs text-muted-foreground">
                  Automatically create the next payment when the current period ends
                </p>
              </div>
              <Switch
                checked={formData.isAutoRecurring}
                onCheckedChange={(checked) => setFormData({ ...formData, isAutoRecurring: checked })}
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            className="bg-cyan-600 hover:bg-cyan-700"
            disabled={isLoading || loadingClients}
          >
            {isLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Create Collection
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}